import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sticker } from "@/components/Stickers";

// Compact "5m / 3h / 2d / Jan 4" timestamp for the inbox row.
function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!isFinite(t)) return "";
  const delta = Math.max(0, (Date.now() - t) / 1000);
  if (delta < 60) return "now";
  if (delta < 3600) return `${Math.floor(delta / 60)}m`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h`;
  if (delta < 604800) return `${Math.floor(delta / 86400)}d`;
  return new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function Messages() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/matches")
      .then(({ data }) => setMatches(data || []))
      .catch((err) => console.warn("matches fetch failed", err))
      .finally(() => setLoading(false));
  }, []);

  const totalUnread = matches.reduce((n, m) => n + (m.unread || 0), 0);

  return (
    <div className="crush-bg min-h-screen" data-testid="messages-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16">
        <Header />
        <div className="crush-frame mt-4 p-6 sm:p-8 relative">
          <Sticker kind="heart" size={64} className="absolute -top-6 -right-4 rotate-12" />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="font-display text-4xl font-bold text-slate-900">Messages</h1>
              <p className="text-slate-600 font-body">
                {matches.length === 0
                  ? "Match first, chat second."
                  : "Your matches. Tap to keep the spark going ✨"}
              </p>
            </div>
            {totalUnread > 0 ? (
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-display font-bold bg-pink-500 text-white"
                data-testid="messages-unread-total"
              >
                <MessageCircle className="w-3.5 h-3.5" /> {totalUnread} unread
              </span>
            ) : null}
          </div>

          <div className="mt-6">
            {loading ? (
              <div className="text-pink-600 font-display">Loading…</div>
            ) : matches.length === 0 ? (
              <div className="text-center py-12">
                <Sticker kind="spark" size={72} className="mx-auto" />
                <p className="mt-3 text-slate-600 font-body">
                  No matches yet. Head to{" "}
                  <button
                    className="text-pink-600 font-bold underline"
                    onClick={() => navigate("/discover")}
                    data-testid="messages-empty-cta"
                  >
                    Discover
                  </button>.
                </p>
              </div>
            ) : (
              <ul className="space-y-2">
                {matches.map((m) => {
                  const last = m.last_message;
                  const isMine = last?.sender_id === user?.id;
                  const previewText = last
                    ? `${isMine ? "You: " : ""}${last.body}`
                    : "Say hi 👋";
                  const unread = m.unread || 0;
                  return (
                    <li
                      key={m.id}
                      className={`flex items-center gap-4 p-3 rounded-2xl border-2 cursor-pointer transition-colors ${
                        unread > 0
                          ? "bg-pink-50 border-pink-200 hover:border-pink-300"
                          : "bg-white border-slate-100 hover:border-pink-200"
                      }`}
                      onClick={() => navigate(`/chat/${m.id}`)}
                      data-testid={`match-row-${m.profile.id}`}
                    >
                      <div className="relative shrink-0">
                        <img
                          src={m.profile.photo_url}
                          alt={m.profile.name}
                          className="w-14 h-14 rounded-2xl object-cover"
                        />
                        {unread > 0 ? (
                          <span
                            className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-pink-500 text-white text-[10px] font-display font-bold grid place-items-center border-2 border-white"
                            data-testid={`unread-badge-${m.profile.id}`}
                          >
                            {unread > 9 ? "9+" : unread}
                          </span>
                        ) : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className={`font-display text-lg leading-tight truncate ${unread > 0 ? "font-bold text-slate-900" : "font-bold text-slate-800"}`}>
                            {m.profile.name}{m.profile.age ? `, ${m.profile.age}` : ""}
                          </div>
                          <span className="text-[11px] font-body text-slate-400 shrink-0">
                            {last ? timeAgo(last.created_at) : timeAgo(m.created_at)}
                          </span>
                        </div>
                        <div
                          className={`text-sm font-body truncate mt-0.5 ${
                            unread > 0 && !isMine ? "text-slate-800 font-semibold" : "text-slate-500"
                          }`}
                          data-testid={`last-preview-${m.profile.id}`}
                        >
                          {previewText}
                        </div>
                      </div>
                      <span className="text-xs text-pink-600 font-display font-bold shrink-0">Chat →</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
